import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Search, ArrowLeft } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Contact";
import { AnimatedBackground } from "@/components/AnimatedBackground";
import { BlogCard } from "@/components/BlogCard";
import { BLOG_POSTS, getAllCategories, getFeaturedPosts, formatHuDate } from "@/data/blog-posts";

export const Route = createFileRoute("/blog/")({
  head: () => ({
    meta: [
      { title: "Blog & Hasznos tudnivalók autósoknak | LAKROSZ Autószerviz" },
      {
        name: "description",
        content:
          "Szerviztippek, használt autó tanácsok, karbantartási és közlekedési tudnivalók a LAKROSZ Autószerviz egri szakembereitől. Gyakorlati cikkek, érthetően.",
      },
      { property: "og:title", content: "Blog | LAKROSZ Autószerviz" },
      {
        property: "og:description",
        content:
          "Szerviztippek és karbantartási tudnivalók egri autószerviz szakemberektől.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://lakroszautoszerviz.com/blog" },
    ],
    links: [{ rel: "canonical", href: "https://lakroszautoszerviz.com/blog" }],
  }),
  component: BlogIndex,
});

function BlogIndex() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<string | null>(null);
  const categories = getAllCategories();
  const featured = getFeaturedPosts(1)[0];

  const filtered = useMemo(() => {
    return BLOG_POSTS.filter((p) => {
      if (activeCat && p.category !== activeCat) return false;
      if (query.trim()) {
        const q = query.trim().toLowerCase();
        return (
          p.title.toLowerCase().includes(q) ||
          p.excerpt.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q)
        );
      }
      return true;
    });
  }, [query, activeCat]);

  return (
    <main className="relative min-h-screen">
      <AnimatedBackground />
      <Navbar />

      <section className="relative pt-36 pb-16 px-6 lg:px-10">
        <div
          className="absolute inset-0 opacity-40 pointer-events-none"
          style={{ background: "var(--gradient-radial)" }}
        />
        <div className="relative max-w-7xl mx-auto">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" />
            Vissza a főoldalra
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="text-xs uppercase tracking-[0.3em] text-primary mb-4">
              ▸ Blog
            </div>
            <h1 className="font-display text-5xl md:text-7xl leading-[0.95]">
              Hasznos tudnivalók <br />
              <span className="gradient-text">autósoknak.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl">
              Szerviztippek, használt autó tanácsok, karbantartás és közlekedési
              tudnivalók — gyakorlatiasan, a műhelyből, érthető nyelven.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Featured */}
      {featured && (
        <section className="relative px-6 lg:px-10 pb-16">
          <div className="relative max-w-7xl mx-auto">
            <Link
              to="/blog/$slug"
              params={{ slug: featured.slug }}
              className="group grid lg:grid-cols-2 gap-6 rounded-3xl overflow-hidden bg-surface border border-border hover:border-primary/50 hover:bg-surface-elevated transition-all"
            >
              <div className="relative aspect-[16/10] lg:aspect-auto overflow-hidden">
                <img
                  src={featured.image}
                  alt={featured.imageAlt}
                  width={1280}
                  height={720}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                />
              </div>
              <div className="flex flex-col justify-center gap-4 p-8 lg:p-12">
                <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-primary">
                  <span className="px-2.5 py-1 rounded-full border border-primary/40">
                    Kiemelt
                  </span>
                  <span>{featured.category}</span>
                </div>
                <h2 className="font-display text-3xl md:text-5xl leading-tight group-hover:text-primary transition-colors">
                  {featured.title}
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  {featured.excerpt}
                </p>
                <div className="text-xs text-muted-foreground">
                  {formatHuDate(featured.date)} · {featured.readMinutes} perc olvasás
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* Filters */}
      <section className="relative px-6 lg:px-10 pb-8">
        <div className="relative max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Keresés a cikkek között..."
                aria-label="Keresés a cikkek között"
                className="w-full pl-10 pr-4 py-3 rounded-full bg-surface border border-border focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 text-sm transition-all"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setActiveCat(null)}
                className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                  activeCat === null
                    ? "bg-primary text-primary-foreground border-primary"
                    : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                }`}
              >
                Összes
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setActiveCat(c)}
                  className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-colors ${
                    activeCat === c
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="relative px-6 lg:px-10 pb-32">
        <div className="relative max-w-7xl mx-auto">
          {filtered.length === 0 ? (
            <div className="text-center py-20 text-muted-foreground">
              Nincs a keresési feltételeknek megfelelő cikk.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((p, i) => (
                <BlogCard key={p.slug} post={p} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}
